import { Injectable, effect, signal } from '@angular/core';

export type ThemeMode = 'light' | 'dark' | 'auto';

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private readonly STORAGE_KEY = 'webshop-theme-config';

  private _mode = signal<ThemeMode>('light');
  private _isDarkMode = signal<boolean>(false);

  readonly mode = this._mode.asReadonly();
  readonly isDarkMode = this._isDarkMode.asReadonly();

  constructor() {
    this.loadThemeFromStorage();
    this.updateDarkMode();
    this.applyThemeClass();

    effect(() => {
      this.updateDarkMode();
    });

    this.setupSystemThemeListener();
  }

  setThemeMode(mode: ThemeMode): void {
    this._mode.set(mode);
    this.saveThemeToStorage();
    this.updateDarkMode();
    this.applyThemeClass();
  }

  toggleTheme(): void {
    const newMode: ThemeMode = this._mode() === 'dark' ? 'light' : 'dark';
    this.setThemeMode(newMode);
  }

  getEffectiveTheme(): 'light' | 'dark' {
  const mode = this._mode();
  return mode === 'auto' ? this.getSystemTheme() : mode;
}

  private loadThemeFromStorage(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        this._mode.set(JSON.parse(stored).mode ?? 'light');
      }
    } catch (error) {
      console.warn('Failed to load theme from storage:', error);
    }
  }

  private saveThemeToStorage(): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify({ mode: this._mode() }));
    } catch (error) {
      console.warn('Failed to save theme to storage:', error);
    }
  }

  private updateDarkMode(): void {
    const isDark = this.getEffectiveTheme() === 'dark';
    this._isDarkMode.set(isDark);
    document.documentElement.classList.toggle('dark', isDark);
    document.documentElement.classList.toggle('light', !isDark);
  }

  private applyThemeClass(): void {
    document.documentElement.classList.remove('yllw-webshop-light', 'yllw-webshop-dark');
    document.documentElement.classList.add(`yllw-webshop-${this.getEffectiveTheme()}`);
  }

  private getSystemTheme(): 'light' | 'dark' {
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return 'light';
  }

  private setupSystemThemeListener(): void {
    if (typeof window !== 'undefined' && window.matchMedia) {
      window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
        if (this._mode() === 'auto') {
          this.updateDarkMode();
          this.applyThemeClass();
        }
      });
    }
  }
}